import { FormBuilder } from '@/components/formBuilder';
import { Box, Flex } from '@chakra-ui/react';
import { useInterviewStageManager } from '../../../../stage-manager';
import { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { SurveyPreviewContent } from '@/components/shared/survey/components/SurveyPreviewContent';
import { CustomValueContainer } from '@/components/ats/EmailTemplates/EmailTemplates';
import { SURVEY_API_SERVICES } from '@/api/survey';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { KTag } from '@/ui-library/components';

const SURVEY_TYPES = [
  { label: 'Candidate survey', value: 'candidate' },
  { label: 'HM survey', value: 'employee' },
];

const getSurveyDetailsInitialValue = (activity, stage) => {
  if (!activity?.id) {
    return {
      label: stage?.name ? `Survey - ${stage?.name}` : '',
      survey_type: 'candidate',
    };
  } else {
    return {
      label: activity?.label || '',
      survey_type: activity?.survey_type || 'candidate',
      survey_id: activity?.form_template?.id || '',
    };
  }
};

interface SurveyDetailsProps {
  formRef?: any;
  activity?: any;
  jobStage?: boolean;
  onSurveyTypeChange?: (type: string) => void;
}

export const SurveyDetails: React.FC<SurveyDetailsProps> = ({
  formRef = null,
  activity = {},
  onSurveyTypeChange,
}) => {
  const { activityAction } = useInterviewStageManager();
  const { stage = {} } = activityAction;
  const containerRef = useRef(null);
  const [selectedSurveyType, setSelectedSurveyType] = useState<string>(
    activity?.survey_type || 'candidate'
  );
  const [selectedSurveyId, setSelectedSurveyId] = useState<number | null>(
    activity?.form_template?.id || null
  );

  const labelStyles = { fontSize: '14px', fontWeight: 'semiBold' };
  const containerStyles = { paddingLeft: '24px', paddingRight: '24px' };

  const activityStatus = activity?.status;
  const isScheduled = activityStatus === 'scheduled';
  const isSent = activityStatus === 'sent';

  const { currentJob } = useSelector(getAtsJobCreate);
  const currentJobData = currentJob?.data || {};
  const { department, office, employment_type } =
    currentJobData?.basic_info || {};

  const filter_by = useMemo(() => {
    if (!currentJobData?.basic_info) return undefined;

    const params: Record<string, any> = {};

    if (department?.id) {
      params.department_ids = {
        is: [department.id],
      };
    }

    if (office?.length) {
      params.office_ids = {
        is: office.map((itr) => itr.id),
      };
    }

    if (employment_type) {
      params.employment_types = {
        is: [employment_type],
      };
    }

    params.status = {
      is: ['published'],
    };

    return params;
  }, [currentJobData?.basic_info, department, office, employment_type]);

  const { data: surveyFormTemplates = { data: [] } } = useQuery({
    queryKey: ['survey', 'form-templates', filter_by],
    queryFn: () =>
      SURVEY_API_SERVICES.getSurveyList(filter_by ? { filter_by } : undefined),
    staleTime: 0,
  });

  const { data: surveyDetails } = useQuery({
    queryKey: ['survey', 'form-template', selectedSurveyId],
    queryFn: () => SURVEY_API_SERVICES.getSurveyById(selectedSurveyId!),
    enabled: !!selectedSurveyId,
    staleTime: 0,
  });

  const surveyFormTemplatesData = useMemo(
    () => surveyFormTemplates?.data?.survey_form_templates || [],
    [surveyFormTemplates?.data]
  );

  // Filter published surveys based on selected survey_type
  const filteredSurveyOptions = useMemo(() => {
    if (!selectedSurveyType) return [];

    return (
      surveyFormTemplatesData
        ?.filter(
          (template) =>
            template.status === 'published' &&
            template.survey_type === selectedSurveyType
        )
        ?.map((template) => ({
          label: template.title,
          value: template.id,
        })) || []
    );
  }, [surveyFormTemplatesData, selectedSurveyType]);

  // Set first survey as default when data loads (for both candidate and employee types)
  useEffect(() => {
    if (
      !activity?.id &&
      (selectedSurveyType === 'candidate' ||
        selectedSurveyType === 'employee') &&
      !selectedSurveyId &&
      filteredSurveyOptions.length > 0 &&
      formRef?.current?.setFieldValue
    ) {
      const firstSurveyId = filteredSurveyOptions[0]?.value;
      if (firstSurveyId) {
        formRef.current.setFieldValue('survey_id', firstSurveyId);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedSurveyId(firstSurveyId);
      }
    }
  }, [
    activity?.id,
    selectedSurveyType,
    selectedSurveyId,
    filteredSurveyOptions,
    formRef,
  ]);

  const handleFormChange = useCallback(
    (values: any) => {
      const surveyId = values?.survey_id;
      const surveyType = values?.survey_type;

      if (surveyType && surveyType !== selectedSurveyType) {
        setSelectedSurveyType(surveyType);
      }

      if (surveyId && surveyId !== selectedSurveyId) {
        setSelectedSurveyId(surveyId);
      } else if (!surveyId && selectedSurveyId) {
        setSelectedSurveyId(null);
      }
    },
    [selectedSurveyType, selectedSurveyId]
  );

  const formConfig = [
    {
      name: 'label',
      label: 'Activity name',
      type: 'kInput',
      isRequired: true,
      placeholder: 'Survey activity name',
      containerStyles: containerStyles,
      labelStyles: labelStyles,
      width: '100%',
      errorMessage: 'Activity name is required',
    },
    {
      name: 'survey_type',
      label: 'Survey type',
      type: 'kSelect',
      isRequired: true,
      placeholder: 'Select survey type',
      containerStyles: containerStyles,
      labelStyles: labelStyles,
      width: '100%',
      options: SURVEY_TYPES,
      disabled: isScheduled || isSent,
      customOnChange: (selectedOption: any) => {
        const newSurveyType = selectedOption?.value || '';
        setSelectedSurveyType(newSurveyType);
        onSurveyTypeChange?.(newSurveyType);
        if (formRef?.current?.setFieldValue) {
          formRef.current.setFieldValue('survey_id', '');
        }
        setSelectedSurveyId(null);
      },
    },
    {
      name: 'survey_id',
      label: 'Select survey',
      type: 'kSelect',
      isRequired: true,
      placeholder: 'Search surveys',
      containerStyles: containerStyles,
      labelStyles: labelStyles,
      width: '100%',
      options: filteredSurveyOptions,
      useInput: true,
      disabled: !selectedSurveyType || isSent,
      customOnChange: (selectedOption: any) => {
        const newSurveyId = selectedOption?.value || null;
        setSelectedSurveyId(newSurveyId);
      },
      components: {
        ValueContainer: CustomValueContainer,
      },
    },
  ];

  // Prepare survey preview data
  const surveyPreviewData = useMemo(() => {
    // The API response might be directly the survey object or wrapped in data
    const surveyData = surveyDetails?.data || surveyDetails;
    if (!surveyData) return null;

    return {
      customFields: surveyData.ats_custom_fields || [],
      fieldGroups: surveyData.field_groups || {},
      surveyType: surveyData.survey_type || selectedSurveyType,
    };
  }, [surveyDetails, selectedSurveyType]);

  return (
    <Flex
      flexDir="column"
      ref={containerRef}
      pb={surveyPreviewData ? '0' : '320px'}
    >
      <FormBuilder
        config={formConfig}
        formRef={formRef}
        initialValues={getSurveyDetailsInitialValue(activity, stage)}
        showCustomError={false}
        observFunc={handleFormChange}
        isObserveEnabled={true}
      />
      {surveyPreviewData && (
        <Box px="8px" pb="80px">
          <Flex alignItems="center" gap="8px">
            <KTag
              py="6px"
              px="16px"
              bgColor="secondary-800"
              color="base-white"
              borderRadius="8px"
              value="Preview"
              ml="16px"
              mb="16px"
            />
            <Box flex="1" height="1px" bg="border-color" mb="16px" />
          </Flex>
          <SurveyPreviewContent
            customFields={surveyPreviewData.customFields}
            fieldGroups={surveyPreviewData.fieldGroups}
            surveyType="employee"
            consumer="job"
          />
        </Box>
      )}
    </Flex>
  );
};
